/**
 * FO-059 (batch-18) — Materieel-bestelling: order-groepering per leverancier.
 *
 * Een gemengde bestelaanvraag (artikelen met verschillende leverancierId's)
 * wordt gegroepeerd per leverancier (Client type=LEVERANCIER) in één
 * bestel-concept per leverancier. Gedeeld request-schema + DTO's, hergebruikt
 * door de API (validatie) en de backoffice (client-side validatie + inline
 * veldfouten). Spiegelt de gereedschapspakket-bestelmail (FO-041) maar op
 * Client-niveau, zodat dezelfde concept-eerst-flow geldt voor gereedschap én
 * apparatuur.
 */

import { z } from 'zod'

export const MATERIEEL_BESTELLING_STATUS = ['CONCEPT', 'VERZONDEN', 'GEANNULEERD'] as const
export type MaterieelBestellingStatus = (typeof MATERIEEL_BESTELLING_STATUS)[number]

export const MATERIEEL_BESTELLING_STATUS_LABELS: Record<MaterieelBestellingStatus, string> = {
  CONCEPT: 'Concept',
  VERZONDEN: 'Verzonden',
  GEANNULEERD: 'Geannuleerd',
}

/** Eén te bestellen regel: artikel + aantal. */
export const materieelBestelRegelInputSchema = z.object({
  artikelId: z.string().min(1),
  aantal: z.number().int().positive(),
})
export type MaterieelBestelRegelInput = z.infer<typeof materieelBestelRegelInputSchema>

/** Genereer-request: een mix van artikelen die per leverancier wordt gesplitst. */
export const genereerMaterieelBestellingenSchema = z.object({
  regels: z.array(materieelBestelRegelInputSchema).min(1, 'Selecteer minstens één artikel'),
  gewensteLeverdatum: z.string().optional().nullable(),
})
export type GenereerMaterieelBestellingenInput = z.infer<
  typeof genereerMaterieelBestellingenSchema
>

/** Verstuur-request: de (bewerkte) concept-mail + ontvanger. */
export const verstuurMaterieelBestellingSchema = z.object({
  onderwerp: z.string().min(1, 'Onderwerp is verplicht').max(300),
  body: z.string().min(1, 'Berichttekst is verplicht'),
  ontvanger: z.string().email('Geen geldig e-mailadres'),
})
export type VerstuurMaterieelBestellingInput = z.infer<
  typeof verstuurMaterieelBestellingSchema
>

export interface MaterieelBestellingRegelDTO {
  id: string
  artikelId: string
  artikelNaam: string
  leverancierArtNr: string | null
  aantal: number
}

export interface MaterieelBestellingDTO {
  id: string
  leverancierId: string
  leverancierNaam: string
  /** Voorgestelde ontvanger (Client.email), kan in de verstuur-stap worden aangepast. */
  leverancierEmail: string | null
  status: MaterieelBestellingStatus
  conceptOnderwerp: string | null
  conceptBody: string | null
  gewensteLeverdatum: string | null
  verzondenOp: string | null
  createdAt: string
  regels: MaterieelBestellingRegelDTO[]
}

/** Eén niet-toewijsbaar artikel (geen leverancier ingesteld). */
export interface OngekoppeldArtikelDTO {
  artikelId: string
  artikelNaam: string
  aantal: number
}

/** Resultaat van genereren: concepten per leverancier + ongekoppelde artikelen. */
export interface GenereerMaterieelResultDTO {
  concepten: MaterieelBestellingDTO[]
  ongekoppeld: OngekoppeldArtikelDTO[]
}

/** Eén besteladvies-regel (artikel onder minimum, met een leverancier). */
export interface MaterieelBesteladviesRegelDTO {
  artikelId: string
  artikelNaam: string
  artikelNummer: string
  categorie: string
  huidigSaldo: number
  minimumVoorraad: number
  tekort: number
  adviesAantal: number
  leverancierId: string | null
  leverancierNaam: string | null
  leverancierArtNr: string | null
}
